import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { HardHat, Thermometer, Receipt, Package, Clock, ChevronRight, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/daily-brief`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '');

function Cell({ icon: Icon, title, count, tone, children, testId, onTitle }) {
  const tones = { sky: 'text-sky-700 bg-sky-50 border-sky-200', rose: 'text-rose-700 bg-rose-50 border-rose-200', emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200', amber: 'text-amber-700 bg-amber-50 border-amber-200' };
  return (
    <div className="rounded-2xl ti-panel flex flex-col min-h-[180px]" data-testid={testId}>
      <button onClick={onTitle} className="flex items-center gap-2 px-4 py-3 border-b border-slate-200/60 text-left w-full hover:bg-white rounded-t-2xl">
        <span className={`w-8 h-8 rounded-lg border flex items-center justify-center ${tones[tone]}`}><Icon className="w-4 h-4" /></span>
        <span className="font-bold text-slate-900 text-sm flex-1">{title}</span>
        <span className={`text-xs font-black px-2 py-0.5 rounded-full border ${tones[tone]}`} data-testid={`${testId}-count`}>{count}</span>
        {onTitle && <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      <div className="p-3 flex-1 overflow-y-auto max-h-80 text-sm">{children}</div>
    </div>
  );
}

export default function OpsCells() {
  const [d, setD] = useState(null);
  const [openBox, setOpenBox] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/ops-cells`, auth()).then(r => setD(r.data)).catch(() => setD(false));
  }, []);
  if (d === null || d === false) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="ops-cells">
      <Cell icon={HardHat} title="On site now" count={d.active_projects.filter(p => p.active_today).length} tone="sky" testId="ops-cell-projects" onTitle={() => navigate('/field-tracker')}>
        {d.active_projects.length === 0 ? <p className="text-slate-500 text-xs">No crews scheduled or clocked in today.</p> : d.active_projects.map(p => (
          <div key={p.work_order_number} className="mb-3 last:mb-0" data-testid={`ops-project-${p.work_order_number}`}>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono font-bold text-slate-900">{p.work_order_number}</span>
              <span className="text-slate-600 text-xs truncate">{p.customer}{p.site ? ` · ${p.site}` : ''}</span>
              {!p.active_today && <span className="text-[9px] font-bold text-slate-500 uppercase">this week</span>}
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(p.on_site.length ? p.on_site : p.scheduled).map((w, i) => (
                <span key={i} className={`text-[11px] px-1.5 py-0.5 rounded-md ${w.live ? 'bg-emerald-100 text-emerald-800' : w.out ? 'bg-slate-200 text-slate-600' : 'bg-sky-100 text-sky-800'}`} title={w.live ? `clocked in ${fmtT(w.in)}` : w.out ? `${fmtT(w.in)}–${fmtT(w.out)}` : 'scheduled'}>
                  {w.live && <Radio className="inline w-2.5 h-2.5 mr-0.5 -mt-0.5" />}{w.name}{w.foreman ? ' ★' : ''}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1">
              <Clock className="w-3 h-3" />
              <span>today <b className="text-slate-900">{p.hours_today}h</b></span>
              <span>week <b className="text-slate-900">{p.hours_week}h</b></span>
              <span>month <b className="text-slate-900">{p.hours_month}h</b>{p.people_month ? ` · ${p.people_month} ppl` : ''}</span>
            </div>
          </div>
        ))}
      </Cell>

      <Cell icon={Thermometer} title="Sick today" count={d.sick_today.length} tone="rose" testId="ops-cell-sick" onTitle={() => navigate('/time-off')}>
        {d.sick_today.length === 0 ? <p className="text-slate-500 text-xs">Nobody has called in sick today.</p> : d.sick_today.map((s, i) => (
          <div key={i} className="mb-2 last:mb-0" data-testid={`ops-sick-${i}`}>
            <div className="font-semibold text-slate-900">{s.name}</div>
            {s.was_due_at ? <div className="text-xs text-rose-700">Was due at <span className="font-mono">{s.was_due_at.work_order_number}</span> {s.was_due_at.customer}{s.was_due_at.site ? ` · ${s.was_due_at.site}` : ''}</div>
              : <div className="text-xs text-slate-500">Not on a site crew today</div>}
            {s.note && <div className="text-[11px] text-slate-500 italic">{s.note}</div>}
          </div>
        ))}
      </Cell>

      <Cell icon={Receipt} title="Invoiced · last 7 days" count={d.recently_invoiced.length} tone="emerald" testId="ops-cell-invoiced" onTitle={() => navigate('/projects')}>
        {d.recently_invoiced.length === 0 ? <p className="text-slate-500 text-xs">Nothing invoiced in the last 7 days.</p> : (
          <>
            {d.recently_invoiced.map((inv, i) => (
              <div key={i} className="flex items-start gap-2 mb-2 last:mb-0" data-testid={`ops-invoice-${inv.our_invoice_number || i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2"><span className="font-mono font-bold text-slate-900 text-xs">{inv.work_order_number}</span><span className="text-slate-600 text-xs truncate">{inv.customer_name}</span></div>
                  <div className="text-[11px] text-slate-500 truncate">{inv.description} · inv {inv.our_invoice_number || '—'} · {fmtD(inv.sent_on)} · {inv.assigned_manager}</div>
                </div>
                <div className="text-right shrink-0"><div className="font-bold text-emerald-700 text-xs">{money(inv.total_amount)}</div><div className="text-[9px] text-slate-500 uppercase">{inv.invoice_status}</div></div>
              </div>
            ))}
            <div className="mt-2 pt-2 border-t border-slate-200/60 text-xs text-slate-600 flex justify-between"><span>Total</span><b className="text-emerald-700">{money(d.recently_invoiced.reduce((s, i) => s + (i.total_amount || 0), 0))}</b></div>
          </>
        )}
      </Cell>

      <Cell icon={Package} title="Tool boxes out" count={d.toolboxes_out.length} tone="amber" testId="ops-cell-boxes" onTitle={() => navigate('/tool-crib')}>
        {d.toolboxes_out.length === 0 ? <p className="text-slate-500 text-xs">Every box is back in the crib.</p> : d.toolboxes_out.map(b => (
          <div key={b.id} className="mb-2 last:mb-0" data-testid={`ops-box-${b.kit_number}`}>
            <button onClick={() => setOpenBox(openBox === b.id ? null : b.id)} className="w-full text-left">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono font-bold text-slate-900 text-xs">{b.kit_number}</span>
                <span className="text-slate-600 text-xs truncate">{b.work_order_number} · {b.customer}{b.site ? ` · ${b.site}` : ''}</span>
                {b.job_over && <span className="text-[9px] font-bold text-amber-600 uppercase">job ended — still out</span>}
              </div>
              <div className="text-[11px] text-slate-500">{b.foreman ? `${b.foreman} · ` : ''}{b.item_count} items{b.ends ? ` · back ${fmtD(b.ends)}` : ''} · {openBox === b.id ? 'hide' : 'what\'s inside'}</div>
            </button>
            {openBox === b.id && (
              <div className="mt-1 flex flex-wrap gap-1" data-testid={`ops-box-items-${b.kit_number}`}>
                {b.items.map((it, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{it}</span>)}
                {b.item_count > b.items.length && <span className="text-[10px] text-slate-500">+{b.item_count - b.items.length} more</span>}
                <button onClick={() => navigate(`/tool-crib?kit=${b.id}`)} className="text-[10px] text-sky-700 font-semibold underline ml-1">open box</button>
              </div>
            )}
          </div>
        ))}
      </Cell>
    </div>
  );
}
